
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Video, Scissors, Film, Music, Zap, Award, Clock, Sparkles } from "lucide-react";

const VideoEditingPage = () => {
  useEffect(() => {
    document.title = "Video Editing Services - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Video className="w-8 h-8 text-blue-600" />,
      title: "Professional Video Editing",
      description: "High-quality video editing for corporate videos, documentaries, and promotional content"
    },
    {
      icon: <Scissors className="w-8 h-8 text-blue-600" />,
      title: "Color Correction & Grading",
      description: "Professional color correction and grading to enhance the visual appeal of your videos"
    },
    {
      icon: <Music className="w-8 h-8 text-blue-600" />,
      title: "Audio Post-Production",
      description: "Audio editing, mixing, and sound design to ensure crystal clear audio quality"
    },
    {
      icon: <Sparkles className="w-8 h-8 text-blue-600" />,
      title: "Visual Effects (VFX)",
      description: "Adding stunning visual effects and motion graphics to enhance your video content"
    },
    {
      icon: <Film className="w-8 h-8 text-blue-600" />,
      title: "Social Media Videos",
      description: "Short-form video content optimized for social media platforms and engagement"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Quick Turnaround",
      description: "Fast and efficient video editing services without compromising on quality"
    }
  ];

  const editingSoftware = [
    { name: "Adobe Premiere Pro", icon: "🎬" },
    { name: "DaVinci Resolve", icon: "🎨" },
    { name: "Final Cut Pro", icon: "🍎" },
    { name: "Adobe After Effects", icon: "✨" },
    { name: "Avid Media Composer", icon: "🎭" },
    { name: "Adobe Audition", icon: "🎵" }
  ];

  const videoTypes = [
    "Corporate Videos",
    "Promotional Videos",
    "Social Media Content",
    "Wedding Videos",
    "Event Coverage",
    "Product Demos",
    "Training Videos",
    "YouTube Content"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional video editing services in Nepal. Corporate videos, promotional content, social media videos, and post-production services." />
        <meta name="keywords" content="video editing, post production, color correction, audio editing, visual effects, Nepal" />
        <meta property="og:title" content="Video Editing Services - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Video Editing Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your raw footage into compelling stories with our professional video editing services.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Editing Software Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Professional Software We Use</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {editingSoftware.map((software, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{software.icon}</div>
                  <h3 className="font-semibold">{software.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Video Types Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Types of Videos We Edit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videoTypes.map((type, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{type}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Editing Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Editing Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Review", desc: "Analyzing your raw footage and requirements" },
                { step: "02", title: "Edit", desc: "Cutting, arranging, and structuring content" },
                { step: "03", title: "Enhance", desc: "Color grading, audio mixing, and effects" },
                { step: "04", title: "Deliver", desc: "Final review and delivery in your format" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Edit Your Video?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's transform your footage into a compelling visual story.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default VideoEditingPage;
